// S25 轉真人收件匣 /ai/handoffs — 客服升級單認領 / 回覆 / 關閉。
//
// 分頁(open/claimed/closed/全部)→ 收件匣列表 → 點列開抽屜看單張詳情 + 往返訊息 →
// 認領 / 回覆 / 關閉(認領、關閉走 ConfirmDialog;回覆行內)。寫後刷新列表與詳情。
// 收件匣動作=AI 域寫入 → 依 canAdminWrite 顯示/隱藏寫入控制。

import { useCallback, useEffect, useRef, useState } from 'react';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { CopyableId } from '../../components/CopyableId';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { actionErrorMessage } from '../_shared/actionError';
import {
  claimHandoff,
  closeHandoff,
  fetchHandoffDetail,
  fetchHandoffs,
  formatCsTime,
  handoffStatusLabel,
  handoffStatusTone,
  replyHandoff,
  senderLabel,
} from './aiOpsApi';
import type { AiHandoffDetail, AiHandoffRow } from './aiOpsApi';
import '../../features/_shared/shared.css';
import './aiOps.css';

const TABS = [
  { value: 'open', label: '待處理' },
  { value: 'claimed', label: '已認領' },
  { value: 'closed', label: '已結單' },
  { value: '', label: '全部' },
];

const HANDOFF_POLL_MS = 30000; // 收件匣背景輪詢週期

export default function HandoffsPage() {
  const { role } = useAuth();
  const writable = canAdminWrite(role);

  const [tab, setTab] = useState('open');
  const [rows, setRows] = useState<AiHandoffRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      fetchHandoffs(tab || undefined, signal)
        .then((r) => setRows(r.handoffs))
        .catch((e) => {
          if (!signal?.aborted) {
            setError(e);
            setRows(null);
          }
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [tab]
  );

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  // 背景輪詢(30s):並行客服認領/關單後,收件匣自動反映,不必手動刷新。
  // 僅更新列、不動 loading/error;背景失敗靜默保留現有列(下次手動刷新才顯錯)。
  useEffect(() => {
    const id = setInterval(() => {
      fetchHandoffs(tab || undefined)
        .then((r) => setRows(r.handoffs))
        .catch(() => {});
    }, HANDOFF_POLL_MS);
    return () => clearInterval(id);
  }, [tab]);

  const showInitialLoading = loading && rows === null;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">轉真人收件匣</h1>
          <p className="page-subtitle">
            客服無法回答而升級的轉接單:認領 → 回覆顧客 → 結單。操作者入平台稽核軌;顧客端由 App 前景 poll 取回覆。
          </p>
        </div>
        <button className="btn-ghost" onClick={() => load()} disabled={loading}>
          {loading ? '載入中…' : '重新整理'}
        </button>
      </div>

      <div className="filter-bar is-sticky" data-testid="handoffs-tabs">
        {TABS.map((tb) => (
          <button
            key={tb.value || 'all'}
            className={tab === tb.value ? 'btn-primary btn-sm' : 'btn-ghost btn-sm'}
            aria-pressed={tab === tb.value}
            onClick={() => setTab(tb.value)}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {showInitialLoading && <LoadingState label="載入收件匣…" />}
      {error != null && rows === null && <ErrorState error={error} onRetry={() => load()} />}

      {rows && rows.length === 0 && <EmptyState title="沒有轉接單" detail={tab === 'open' ? '目前沒有待處理的升級單。' : '此分頁沒有資料。'} />}

      {rows && rows.length > 0 && (
        <div className="table-wrap" data-testid="handoffs-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>單號</th>
                <th>問句</th>
                <th>狀態</th>
                <th>指派</th>
                <th>訊息</th>
                <th>更新時間</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((h) => (
                <tr
                  key={h.id}
                  className="clickable"
                  tabIndex={0}
                  onClick={() => setOpenId(h.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setOpenId(h.id);
                  }}
                >
                  <td className="adm-mono adm-cell-strong">#{h.id}</td>
                  <td className="aiops-cell-query" title={h.query ?? ''}>
                    {h.query || '—'}
                  </td>
                  <td>
                    <span className={`pill ${handoffStatusTone(h.status)}`}>{handoffStatusLabel(h.status)}</span>
                  </td>
                  <td className="text-secondary">{h.assigned_to || '—'}</td>
                  <td className="adm-mono">{h.messages}</td>
                  <td className="adm-mono">{formatCsTime(h.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="adm-pager">
            <span className="text-secondary">共 {rows.length} 筆</span>
          </div>
        </div>
      )}

      {openId != null && (
        <HandoffDrawer
          id={openId}
          writable={writable}
          onClose={() => setOpenId(null)}
          onChanged={() => load()}
        />
      )}
    </>
  );
}

// ── 抽屜:單張詳情 + 往返訊息 + 動作 ─────────────────────────────────────────

type PendingAction = 'claim' | 'close' | null;

function HandoffDrawer({ id, writable, onClose, onChanged }: { id: number; writable: boolean; onClose: () => void; onChanged: () => void }) {
  const [detail, setDetail] = useState<AiHandoffDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  const [reply, setReply] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [pending, setPending] = useState<PendingAction>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const refetch = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      fetchHandoffDetail(id, signal)
        .then(setDetail)
        .catch((e) => {
          if (!signal?.aborted) setError(e);
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id]
  );

  useEffect(() => {
    const ac = new AbortController();
    refetch(ac.signal);
    return () => ac.abort();
  }, [refetch]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !confirmBusy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, confirmBusy]);

  const submitReply = () => {
    if (!reply.trim()) return;
    setReplyBusy(true);
    setActionError(null);
    replyHandoff(id, reply.trim())
      .then(() => {
        setReply('');
        refetch();
        onChanged();
      })
      .catch((e) => setActionError(actionErrorMessage(e)))
      .finally(() => setReplyBusy(false));
  };

  const runAction = () => {
    if (!pending) return;
    setConfirmBusy(true);
    setConfirmError(null);
    const fn = pending === 'claim' ? claimHandoff(id) : closeHandoff(id);
    fn.then(() => {
      setPending(null);
      refetch();
      onChanged();
    })
      .catch((e) => setConfirmError(actionErrorMessage(e)))
      .finally(() => setConfirmBusy(false));
  };

  const status = detail?.status;

  return (
    <div className="aiops-drawer-scrim" role="presentation" onClick={() => !confirmBusy && onClose()}>
      <aside className="aiops-drawer" role="dialog" aria-modal="true" aria-label="轉接單詳情" onClick={(e) => e.stopPropagation()} data-testid="handoff-drawer">
        <div className="aiops-drawer-head">
          <h2 className="aiops-drawer-title">
            轉接單 #{id}
            {detail && <span className={`pill ${handoffStatusTone(detail.status)}`}>{handoffStatusLabel(detail.status)}</span>}
          </h2>
          <button ref={closeRef} className="btn-ghost btn-sm aiops-drawer-close" onClick={onClose} aria-label="關閉">
            關閉
          </button>
        </div>

        {loading && detail === null && <LoadingState label="載入轉接單…" />}
        {error != null && detail === null && <ErrorState error={error} onRetry={() => refetch()} />}

        {detail && (
          <>
            <div className="aiops-block">
              <span className="aiops-block-label">升級問句</span>
              <p className="aiops-quote">{detail.query || '—'}</p>
              <div className="aiops-meta-grid">
                <div className="aiops-meta-item">
                  <span className="aiops-meta-k">顧客</span>
                  <span className="aiops-meta-v">{detail.user_id || '匿名'}</span>
                </div>
                <div className="aiops-meta-item">
                  <span className="aiops-meta-k">指派</span>
                  <span className="aiops-meta-v">{detail.assigned_to || '—'}</span>
                </div>
                <div className="aiops-meta-item">
                  <span className="aiops-meta-k">建立</span>
                  <span className="aiops-meta-v">{formatCsTime(detail.created_at)}</span>
                </div>
                <div className="aiops-meta-item">
                  <span className="aiops-meta-k">對話</span>
                  <span className="aiops-meta-v">
                    <CopyableId value={detail.conversation_id} />
                  </span>
                </div>
              </div>
            </div>

            <div className="aiops-block">
              <span className="aiops-block-label">往返訊息（{detail.messages.length}）</span>
              {detail.messages.length > 0 ? (
                <div className="aiops-thread">
                  {detail.messages.map((m) => (
                    <div key={m.id} className={`aiops-msg ${m.sender === 'staff' ? 'is-staff' : 'is-customer'}`}>
                      <div className="aiops-msg-body">{m.body}</div>
                      <div className="aiops-msg-meta">
                        {senderLabel(m.sender)} · {formatCsTime(m.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="aiops-hint">尚無往返訊息。</p>
              )}
            </div>

            {writable ? (
              status !== 'closed' ? (
                <>
                  <div className="aiops-block">
                    <span className="aiops-block-label">回覆顧客</span>
                    <div className="aiops-reply-box">
                      <textarea placeholder="輸入客服回覆…" value={reply} onChange={(e) => setReply(e.target.value)} />
                      {actionError && <p className="error-text" style={{ margin: 0 }}>{actionError}</p>}
                      <div className="aiops-actions-row">
                        <button className="btn-primary" onClick={submitReply} disabled={replyBusy || !reply.trim()}>
                          {replyBusy ? '送出中…' : '送出回覆'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="aiops-actions-row">
                    {status === 'open' && (
                      <button className="btn-ghost" onClick={() => setPending('claim')}>
                        認領
                      </button>
                    )}
                    <button className="btn-ghost-warn" onClick={() => setPending('close')}>
                      關閉此單
                    </button>
                  </div>
                </>
              ) : (
                <p className="aiops-hint">此單已結單,唯讀。</p>
              )
            ) : (
              <p className="aiops-hint">唯讀角色:僅檢視轉接單與訊息,無法認領／回覆／關閉。</p>
            )}
          </>
        )}

        {pending && (
          <ConfirmDialog
            title={pending === 'claim' ? '認領此轉接單' : '關閉此轉接單'}
            tone={pending === 'claim' ? 'primary' : 'warn'}
            confirmLabel={pending === 'claim' ? '認領' : '關閉'}
            busy={confirmBusy}
            error={confirmError}
            warning={
              pending === 'claim'
                ? '此單將指派給你(status → claimed)。'
                : '此單將結單(status → closed),顧客端不再視為待處理。'
            }
            body={<span>{pending === 'claim' ? '確定要認領這張轉接單嗎？' : '確定要關閉這張轉接單嗎？'}</span>}
            onConfirm={runAction}
            onCancel={() => {
              if (!confirmBusy) {
                setPending(null);
                setConfirmError(null);
              }
            }}
          />
        )}
      </aside>
    </div>
  );
}
